import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {map, Observable, tap} from "rxjs";
import {LoaderService} from "./loader.service";
import {ActionResult, RACE_COMMERCIAL_ITEM_DEFAULT, RaceAdditionalPricingItem, RaceCommercialItem} from "@model";
import {EnvironmentService} from "./environment.service";

@Injectable({ providedIn:'root' })
export class RaceCommercialService {
  constructor(private http: HttpClient, private loaderService: LoaderService, private environmentService:EnvironmentService) {
  }

  findRaceCommercialItems(raceId: number, hideLoading?: boolean): Observable<RaceCommercialItem[]> {
    if (!hideLoading) {
      this.loaderService.startLoading();
    }
    return this.http.get<ActionResult<RaceCommercialItem[]>>(this.environmentService.getApiUrl() + "/api/pricing/race/fetch?raceId=" + raceId).pipe(tap(() => {
        if (!hideLoading) {
          this.loaderService.dismissLoading();
        }
      }),
      map((value: ActionResult<RaceCommercialItem[]>) => {
        if (value && value.result && value.result.length != 0) {
          return value.result;
        } else {
          return [];
        }
      })
    );
  }

  findPublicRaceCommercialItems(raceId: number, couponCode?: string): Observable<RaceCommercialItem[]> {
    return this.http.get<ActionResult<RaceCommercialItem[]>>(this.environmentService.getApiUrl() + "/api/public/pricing/race/fetch?raceId=" + raceId + (couponCode ? "&couponCode=" + couponCode : "")).pipe(
      map((value: ActionResult<RaceCommercialItem[]>) => {
        if (value && value.result && value.result.length != 0) {
          return (value.result as RaceCommercialItem[]).filter((item: RaceCommercialItem) => RaceCommercialItem.isVisible(item));
        } else {
          return [];
        }
      })
    );
  }

  updateRaceCommercialItem(raceCommercialItem: RaceCommercialItem, onSuccess: () => void, onError: () => void): void {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>
    (this.environmentService.getApiUrl() + (raceCommercialItem.id ? "/api/pricing/update" : "/api/pricing/add"), raceCommercialItem).pipe(tap(() => this.loaderService.dismissLoading()))
      .subscribe((actionResult: ActionResult<boolean>) => {
        if (actionResult.resultCode !== -1) {
          onSuccess();
        } else {
          onError();
        }
      });
  }

  deleteRaceCommercialItem(raceCommercialItem: RaceCommercialItem, onSuccess: () => void, onError: () => void): void {
    this.loaderService.startLoading();
    this.http.delete<ActionResult<boolean>>
    (this.environmentService.getApiUrl() + "/api/pricing/delete?itemId=" + raceCommercialItem.id).pipe(tap(() => this.loaderService.dismissLoading()))
      .subscribe((actionResult: ActionResult<boolean>) => {
        if (actionResult.resultCode !== -1) {
          onSuccess();
        } else {
          onError();
        }
      });
  }

  sortRaceCommercialItem(raceCommercialItems: RaceCommercialItem[] | undefined): RaceCommercialItem[] {
    if (!raceCommercialItems || raceCommercialItems.length === 0) {
      return [];
    }
    let sortedRaceCommercialItems: RaceCommercialItem[] = [];
    let defaultRaceCommercialItem: RaceCommercialItem | undefined = raceCommercialItems.find((raceCommercialItem: RaceCommercialItem) => raceCommercialItem.registrationFeeTypeId === RACE_COMMERCIAL_ITEM_DEFAULT);
    if (defaultRaceCommercialItem) {
      sortedRaceCommercialItems.push(defaultRaceCommercialItem);
    }
    sortedRaceCommercialItems = [...sortedRaceCommercialItems, ...raceCommercialItems.filter((raceCommercialItem: RaceCommercialItem) => raceCommercialItem.registrationFeeTypeId !== RACE_COMMERCIAL_ITEM_DEFAULT)];
    return sortedRaceCommercialItems;
  }

  findRaceAdditionalPricingItems(raceId: number, hideLoading?: boolean): Observable<RaceAdditionalPricingItem[]> {
    if (!hideLoading) {
      this.loaderService.startLoading();
    }
    return this.http.get<ActionResult<RaceAdditionalPricingItem[]>>(this.environmentService.getApiUrl() + "/api/pricing/items/race/fetch?raceId=" + raceId).pipe(tap(() => {
      if (!hideLoading) {
        this.loaderService.dismissLoading();
      }
    }), map((value: ActionResult<RaceAdditionalPricingItem[]>) => {
      if (value && value.result && value.result.length != 0) {
        return value.result;
      } else {
        return [];
      }
    }));
  }

  findPublicRaceAdditionalPricingItems(raceId: number, couponCode?: string): Observable<RaceAdditionalPricingItem[]> {
    return this.http.get<ActionResult<RaceAdditionalPricingItem[]>>(this.environmentService.getApiUrl() + "/api/public/pricing/items/race/fetch?raceId=" + raceId + (couponCode ? "&couponCode=" + couponCode : "")).pipe(
      map((value: ActionResult<RaceAdditionalPricingItem[]>) => {
        if (value && value.result && value.result.length != 0) {
          return value.result;
        } else {
          return [];
        }
      }));
  }

  updateRaceAdditionalPricingItem(raceAdditionalPricingItem: RaceAdditionalPricingItem, onSuccess: () => void, onError: () => void): void {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>
    (this.environmentService.getApiUrl() + (raceAdditionalPricingItem.id ? "/api/pricing/items/update" : "/api/pricing/items/add"), raceAdditionalPricingItem).pipe(tap(() => this.loaderService.dismissLoading())).subscribe((actionResult: ActionResult<boolean>) => {
      if (actionResult.resultCode !== -1) {
        onSuccess();
      } else {
        onError();
      }
    });
  }

  deleteRaceAdditionalPricingItem(raceAdditionalPricingItem: RaceAdditionalPricingItem, onSuccess: () => void, onError: () => void): void {
    this.loaderService.startLoading();
    this.http.delete<ActionResult<boolean>>
    (this.environmentService.getApiUrl() + "/api/pricing/items/delete?itemId=" + raceAdditionalPricingItem.id).pipe(tap(() => this.loaderService.dismissLoading()))
      .subscribe((actionResult: ActionResult<boolean>) => {
        if (actionResult.resultCode !== -1) {
          onSuccess();
        } else {
          onError();
        }
      });
  }
}
